import React from 'react';
import {useParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PendingContractorsList} from 'src/pages/Modules/Vendors/VendorList/components/PendingContractorsList';
import {VendorList} from 'src/pages/Modules/Vendors/VendorList/components/VendorList';
import {VendorPersonnelList} from 'src/pages/Modules/Vendors/VendorList/components/VendorPersonnelList';
import {VendorDocumentTitle} from 'src/pages/Modules/Vendors/shared/vendorHelpers';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {VendorIndexViewType} from 'src/shared/constants/ViewTypes';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';

import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const VendorListPage: React.FC = () => {
  const navigate = useAppNavigate();
  useSetDocumentTitle(VendorDocumentTitle);
  const {view} = useParams();
  const personnelPerms = usePersonnelPermissions();

  const {RenderedTabsComponent} = useUrlQueryTabV2<VendorIndexViewType>({
    view: (view ?? '') as VendorIndexViewType,
    defaultView: 'list',
    tabs: [
      {
        view: 'list',
        label: 'Vendors',
        component: (
          <>
            <VendorList />
          </>
        ),
      },
      {
        view: 'personnel',
        label: 'Personnel',
        disabled: !personnelPerms.view,
        component: (
          <>
            <VendorPersonnelList
              disableImport={true}
              onRowClick={handlePersonnelRowClick}
            />
          </>
        ),
      },
      {
        view: 'pending',
        label: 'Pending Review',
        disabled: !personnelPerms.view,
        component: (
          <>
            <PendingContractorsList />
          </>
        ),
      },
    ],
    onSelectView: view =>
      navigate(PathUtility.Vendors(view as VendorIndexViewType)),
  });

  function handlePersonnelRowClick(id: number): void {
    navigate(PathUtility.VendorListPersonnelView(id, 'overview'));
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Vendors}>
      {RenderedTabsComponent}
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Vendors(':view'),
  element: <VendorListPage />,
  module: ModuleEnum.Vendors,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default VendorListPage;
